import uniqBy from 'lodash/uniqBy';

import { isUnauthorizedError } from '@confluence/error-boundary';
import { getApolloClient, markErrorAsHandled } from '@confluence/graphql';
import { NoNetworkError } from '@confluence/network';
import {
	handleAddedInlineCommentForUnread,
	handleObliteratedInlineCommentForUnread,
} from '@confluence/unread-comments';

import { QuickReloadGQLQuery, QuickReloadPubSubQuery } from './QuickReloadQuery.graphql';
import type { QuickReloadQuery, QuickReloadQueryVariables } from './__types__/QuickReloadGQLQuery';
import type { PubSubUserQuery, PubSubUserQueryVariables } from './__types__/QuickReloadPubSubQuery';
import type {
	MissedNotificationResult,
	CommentLocation,
	OnInlineCommentActionEvent,
	OnInlineCommentActionParams,
	OnCreatedCommentEvent,
	OnPageCommentActionParams,
} from './pubSubDataTypes';
import type { StreamEvent } from './QuickReloadStreams';
import { PageMode, PubSubFlagType } from './pubSubConstants';

export type UserInfo = {
	displayName: string;
	profilePicturePath: string;
};

export const getUserInfo = async (accountId: string): Promise<UserInfo | undefined> => {
	try {
		const { data } = await getApolloClient().query<PubSubUserQuery, PubSubUserQueryVariables>({
			query: QuickReloadPubSubQuery,
			variables: {
				accountId,
			},
			fetchPolicy: 'cache-first',
		});

		const userInfo = data?.confluenceUsers?.nodes?.[0];
		return userInfo
			? {
					displayName: userInfo?.displayName ?? '',
					profilePicturePath: userInfo?.profilePicture?.path ?? '',
				}
			: undefined;
	} catch (e) {
		if (isUnauthorizedError(e) || e instanceof NoNetworkError) {
			markErrorAsHandled(e);
		}
	}
};

// This creates a href link that a user can click to take them to the comment within the flag.
export const getCommentLink = (commentId: string | null | undefined) =>
	`${location!.pathname}?focusedCommentId=${commentId}`;

export const fetchNotificationsSinceTime = async (
	contentId: string,
	since: number,
	pageMode: PageMode,
): Promise<MissedNotificationResult | undefined> => {
	try {
		const response = await getApolloClient().query<QuickReloadQuery, QuickReloadQueryVariables>({
			query: QuickReloadGQLQuery,
			variables: {
				contentId,
				since,
			},
			fetchPolicy: 'no-cache',
			errorPolicy: 'all',
			context: {
				fetchOptions: {
					ignoreNoNetworkError: true,
				},
				single: true,
			},
		});

		const comments = response.data?.quickReload?.comments || [];
		const commentEvents = comments.map((comment) => {
			const accountId =
				'accountId' in comment.comment.author ? comment.comment.author?.accountId : undefined;
			const commentId = comment.comment?.id;
			const url = getCommentLink(commentId);
			const displayName = comment.comment.author?.displayName;
			const profilePicturePath = comment.comment.author?.profilePicture?.path;
			const isInline = comment.comment?.location.type === 'INLINE';
			const markerRef =
				'inlineMarkerRef' in comment.comment?.location
					? comment.comment?.location.inlineMarkerRef
					: null;
			const adfBodyContent = comment.comment?.body.value;
			const commentType = comment.comment?.parentId ? 'REPLY' : 'TOP_LEVEL';
			const commentCreatedDate = comment.comment?.createdAtNonLocalized;
			return {
				id: commentId,
				displayName,
				profilePicturePath,
				url,
				accountId,
				type: (isInline ? 'INLINE' : 'FOOTER') as CommentLocation,
				adfBodyContent,
				commentType,
				markerRef,
				commentCreatedDate,
			};
		});

		const editorForPage = response.data?.quickReload?.editorForPage;

		let editEvents = new Array();

		// editorForPage is just the last used attributed to the latest content update. For current page, this will be the user who published it, for draft it will be the user who sent the draft sync
		// for Renderer: we want to show the flag when a user has published new edits
		// for Editor, we are handling the publish flag separately, it shouldn't be handled here
		if (editorForPage && pageMode === PageMode.RENDERER) {
			const displayName = editorForPage.displayName || '';
			const accountId = editorForPage.accountId || null;
			const profilePicturePath = editorForPage.profilePicture?.path || '';
			editEvents = [{ displayName, accountId, profilePicturePath }];
		}

		const time = response.data?.quickReload?.time;

		return { commentEvents, time, editEvents, contentId };
	} catch (e) {
		if (isUnauthorizedError(e) || e instanceof NoNetworkError) {
			markErrorAsHandled(e);
		}
		/**
		 * Don't do anything. By virtue of which, we're not returning a timestamp,
		 * which means the poller won't advance its 'since' time, and will re-request
		 * from the same start time.
		 */
		return;
	}
};

export const getUniqueUsers = (eventsExcludingCurrentUserEvents: StreamEvent[]) => {
	return uniqBy(eventsExcludingCurrentUserEvents.slice().reverse(), 'accountId').map(
		({ displayName, profilePicturePath }) => ({
			displayName,
			profilePicturePath,
		}),
	);
};

export const onInlineCommentAction = async (
	_eventName: string,
	eventData: OnInlineCommentActionEvent,
	params: OnInlineCommentActionParams,
) => {
	const { inlineCommentRequestBody, accountId } = eventData;
	const { inlineCommentType, markerRef, commentId } = inlineCommentRequestBody;

	const {
		currentAccountId,
		setPubSubData,
		isUnreadCommentsEnabled,
		updateUnreadCommentsListState,
		readCommentsListState,
		markerRefToCommentIdMap,
		updateMarkerRefToCommentIdMap,
		documentNeedsUpdate,
		pageMode,
		isFabricPage,
	} = params;

	const isNotCurrentUser = !!currentAccountId && accountId !== currentAccountId;

	const isTopLevelComment = inlineCommentType === 'TOP_LEVEL';
	const handleInlineActionParams = {
		isUnreadCommentsEnabled,
		inlineCommentRequestBody,
		readCommentsListState,
		isNotCurrentUser,
		updateUnreadCommentsListState,
		markerRefToCommentIdMap,
		updateMarkerRefToCommentIdMap,
		isTopLevelComment,
	};

	switch (_eventName) {
		case 'avi:confluence:created:inline_comment':
		case 'avi:confluence:created:editor_inline_comment':
		case 'avi:confluence:created:renderer_inline_comment': {
			// In Renderer, don't show flags for top level comments created in Editor or replies created in Editor that don't have a marker ref yet
			const editorCreatedTopLevelComment =
				_eventName === 'avi:confluence:created:editor_inline_comment' && isTopLevelComment;

			const editorCreatedReplyWithNoMarkerRef =
				_eventName === 'avi:confluence:created:editor_inline_comment' &&
				markerRef &&
				!document.getElementById(markerRef);

			if (
				pageMode === PageMode.RENDERER &&
				(editorCreatedTopLevelComment || editorCreatedReplyWithNoMarkerRef)
			) {
				return;
			}

			void getUserInfo(accountId).then((userInfo: UserInfo | undefined) => {
				setPubSubData({
					url:
						eventData.inlineCommentRequestBody?.url ??
						getCommentLink(inlineCommentRequestBody?.commentId),
					displayName: userInfo?.displayName,
					profilePicturePath: userInfo?.profilePicturePath,
					accountId,
					flagType: PubSubFlagType.COMMENT,
					commentId,
					isInline: true,
					markerRef,
					adfBodyContent: inlineCommentRequestBody?.adfBodyContent,
					commentType: inlineCommentRequestBody?.inlineCommentType,
				});
			});

			if (isUnreadCommentsEnabled && !documentNeedsUpdate.current && markerRef && commentId) {
				if (pageMode === PageMode.RENDERER && !isFabricPage) break;
				updateMarkerRefToCommentIdMap((prevState) => {
					const newMarkerRefMap = { ...prevState };

					if (inlineCommentType === 'TOP_LEVEL' && !newMarkerRefMap[markerRef]) {
						newMarkerRefMap[markerRef] = new Set();
					} else if (newMarkerRefMap[markerRef]) {
						newMarkerRefMap[markerRef].add(commentId);
					}
					return newMarkerRefMap;
				});

				handleAddedInlineCommentForUnread(handleInlineActionParams);
			}
			break;
		}
		case 'avi:confluence:unresolved:inline_comment':
		case 'avi:confluence:reattach:inline_comment': {
			if (pageMode === PageMode.RENDERER && !isFabricPage) break;
			handleAddedInlineCommentForUnread({
				...handleInlineActionParams,
				isTopLevelComment: true,
			});
			break;
		}
		case 'avi:confluence:resolved:inline_comment': {
			if (pageMode === PageMode.RENDERER && !isFabricPage) break;
			handleObliteratedInlineCommentForUnread({
				...handleInlineActionParams,
				isTopLevelComment: true,
			});
			break;
		}
		case 'avi:confluence:deleted:inline_comment': {
			if (pageMode === PageMode.RENDERER && !isFabricPage) break;
			handleObliteratedInlineCommentForUnread(handleInlineActionParams);
			break;
		}
		default:
			return;
	}
};

export const shouldAddPageCommentEvents = (
	isPageCommentsAutoReloadEnabled: boolean,
	isPageComment: boolean,
	isNotViewingPageComments: boolean,
) => {
	return isPageCommentsAutoReloadEnabled && isPageComment && isNotViewingPageComments;
};

export const onPageCommentAction = (
	_eventName,
	eventData: OnCreatedCommentEvent,
	params: OnPageCommentActionParams,
) => {
	const { setPubSubData } = params;

	void getUserInfo(eventData.accountId).then((userInfo: UserInfo | undefined) => {
		const { commentRequestBody, accountId } = eventData;
		setPubSubData({
			displayName: userInfo?.displayName,
			profilePicturePath: userInfo?.profilePicturePath,
			url: commentRequestBody?.url ?? getCommentLink(commentRequestBody?.commentId),
			accountId,
			flagType: PubSubFlagType.COMMENT,
			commentId: commentRequestBody?.commentId,
			adfBodyContent: commentRequestBody?.adfBodyContent,
			commentType: commentRequestBody?.pageCommentType,
			isInline: false,
		});
	});
};
